import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useParams } from "react-router-dom";
import { loginUser } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { LogIn, Loader2, ArrowLeft, Mail, Lock } from "lucide-react";

export default function LoginPage() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await loginUser(email, password);
      toast({ title: t("auth.login_success") });
      navigate(`/${lang}`);
    } catch (err) {
      setError(err.message || t("auth.login_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Dekoratif arka plan */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[100px]" />
      </div>

      {/* Geri dön linki */}
      <Link
        to={`/${lang}`}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-sm z-10"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("header.properties") ? "Ana Sayfa" : "Home"}
      </Link>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to={`/${lang}`}>
            <h1
              className="text-3xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Oly<span className="text-[#D4AF37]">Investment</span>
            </h1>
          </Link>
          <p className="text-white/50 text-sm mt-2">KKTC Gayrimenkul & Yatirim</p>
        </div>

        {/* Giriş Kartı */}
        <Card className="bg-white/[0.03] border-white/10 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <LogIn className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <CardTitle className="text-xl font-semibold text-white">
              {t("auth.login")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-sm">
                  {t("auth.email")}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Şifre */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-white/70 text-sm">
                    {t("auth.password")}
                  </Label>
                  <button
                    type="button"
                    className="text-xs text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors"
                  >
                    {t("auth.forgot_password")}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
              </div>

              {/* Hata mesajı */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              {/* Giriş butonu */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-white font-medium h-11 text-sm transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/20"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {t("auth.login")}
                  </>
                )}
              </Button>
            </form>

            <Separator className="my-6 bg-white/10" />

            {/* Kayıt linki */}
            <div className="text-center">
              <p className="text-white/50 text-sm">
                {t("auth.no_account")}{" "}
                <Link
                  to={`/${lang}/kayit`}
                  className="text-[#D4AF37] hover:text-[#B5952F] font-medium transition-colors"
                >
                  {t("auth.register")}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Alt bilgi */}
        <p className="text-center text-white/30 text-xs mt-6">
          &copy; {new Date().getFullYear()} OlyInvestment. {t("footer.copyright")}
        </p>
      </div>
      <Toaster />
    </div>
  );
}
